import React, { useState, useEffect, useRef } from 'react';
import Icon from './AppIcon';
import AgentStatusIndicator from './AgentStatusIndicator';

const ActivityFeed = ({ 
  className = '',
  maxItems = 50,
  autoRefresh = true,
  refreshInterval = 30000
}) => {
  const [activities, setActivities] = useState([]);
  const [filter, setFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const scrollContainerRef = useRef(null);

  const activityTypes = [
    { value: 'all', label: 'All Activities', icon: 'Activity' },
    { value: 'agent', label: 'Agent Actions', icon: 'Users' },
    { value: 'task', label: 'Tasks', icon: 'CheckSquare' },
    { value: 'system', label: 'System', icon: 'Settings' }
  ];

  useEffect(() => {
    // No mock data - start with empty activities
    setActivities([]);
  }, []);

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        // Real-time refresh would fetch from database here
        // For now, keep activities empty
      }, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval, maxItems]);

  const filteredActivities = activities?.filter(activity => 
    filter === 'all' || activity?.type === filter
  );

  const formatTimestamp = (date) => {
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);

    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const handleLoadMore = () => {
    setIsLoading(true);
    setTimeout(() => {
      setActivities(prev => [...activities, ...activities]);
      setIsLoading(false);
    }, 1000);
  };

  return (
    <div className={`bg-card rounded-xl border border-border shadow-warm ${className}`}>
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Activity" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground font-heading">
                Activity Feed
              </h3>
              <p className="text-sm text-muted-foreground font-caption">
                Real-time agent operations
              </p>
            </div>
          </div>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-2">
          {activityTypes?.map((type) => (
            <button
              key={type?.value}
              onClick={() => setFilter(type?.value)}
              className={`
                flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm
                transition-all duration-250 whitespace-nowrap
                ${filter === type?.value 
                  ? 'bg-primary text-primary-foreground shadow-warm-sm' 
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }
              `}
            >
              <Icon name={type?.icon} size={16} />
              <span className="font-caption">{type?.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div 
        ref={scrollContainerRef}
        className="max-h-96 overflow-y-auto custom-scrollbar"
      >
        {filteredActivities?.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
              <Icon name="Inbox" size={32} color="var(--color-muted-foreground)" />
            </div>
            <p className="text-muted-foreground font-caption">
              No activities yet
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Agent activities will appear here as they occur
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filteredActivities?.map((activity) => (
              <div 
                key={activity?.id}
                className="p-4 hover:bg-muted/30 transition-colors duration-250"
              >
                <div className="flex items-start gap-4">
                  <div className={`flex-shrink-0 w-10 h-10 rounded-lg bg-muted flex items-center justify-center ${activity?.color}`}>
                    <Icon name={activity?.icon} size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-foreground text-sm">
                        {activity?.agentName}
                      </span>
                      <AgentStatusIndicator 
                        status={activity?.agentStatus}
                        size="sm"
                        showLabel={false}
                      />
                    </div>
                    <p className="text-sm font-medium text-foreground mb-1">
                      {activity?.action}
                    </p>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {activity?.description}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <Icon name="Clock" size={12} color="var(--color-muted-foreground)" />
                      <span className="text-xs text-muted-foreground font-caption">
                        {formatTimestamp(activity?.timestamp)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {filteredActivities?.length > 0 && (
        <div className="p-4 border-t border-border">
          <button
            onClick={handleLoadMore}
            disabled={isLoading}
            className="w-full py-3 px-4 rounded-lg bg-muted hover:bg-muted/80 text-foreground font-medium text-sm transition-all duration-250 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <Icon name="Loader2" size={16} className="animate-spin" />
                Loading...
              </span>
            ) : (
              'Load More Activities'
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default ActivityFeed;